import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';

import { TableComponent } from '../../ui/table/table.component';
import { PageNavigationService } from '../../../service/app-state/page-navigation.service';
import { ToolbarService } from '../../../service/app-state/toolbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableFormArrayCache } from '../../../service/table-form-array-cache.service';
import { CurrentTableCell } from '../../../models/interfaces';
import { ExtractedRowForm } from '../../../models/extraction-form-models';
import { TableView } from '../table-view';
import { AbstractControl, FormArray, ValidatorFn } from '@angular/forms';
import { OcrFormInputDirective } from '../../../directives/ocr-form-input.directive';
import { ExtractionField } from '../../../pages/extraction-page/extraction-page.component';
import {
  FieldModel,
  getTableModelByColumnKey,
} from '../../../models/extraction-models/extraction-models';
import { CtBatchStoreService } from '../../../service/app-state/ct-batch-store.service';
import { isDefined } from '../../../utils/TypeGuard.utils';
import { ExtractedValue } from '../../../models/extracted-values';

@Component({
  selector: 'ct-extraction-data-capturing-position-type',
  imports: [TableComponent, OcrFormInputDirective],
  templateUrl: './data-capturing-row-type.component.html',
})
export class DataCapturingRowTypeComponent extends TableView implements OnInit {
  private readonly pageNavigationService = inject(PageNavigationService);
  private readonly toolBarService = inject(ToolbarService);
  private readonly batchStoreService = inject(CtBatchStoreService);

  protected readonly destroyRef = inject(DestroyRef);
  protected readonly tableFormArrayCache = inject(TableFormArrayCache);

  continue = output();

  protected readonly currentTableCell = signal<CurrentTableCell | undefined>(undefined);

  readonly currentRowToClassify = signal<ExtractedRowForm | undefined>(undefined);

  private readonly allRowsClassifiedValidator: ValidatorFn = (control: AbstractControl) => {
    if (!this.currentTableModel().rowClassification) return null;

    return (control as FormArray<ExtractedRowForm>).controls.every((rowGroup) => {
      return !!rowGroup.controls.class.value;
    })
      ? null
      : { unclassifiedRows: true };
  };

  private readonly inlineConsolidatorKeyForFieldMap = computed(() =>
    this.currentTableModel()
      .fieldModels.filter((model) => model.key && model.inlineConsolidatorForField)
      .reduce((acc: Record<string, string>, entry: FieldModel<string, ExtractedValue>) => {
        acc[entry.key] = entry.inlineConsolidatorForField!;
        return acc;
      }, {}),
  );

  private readonly inlineConsolidatorKeys = computed(() =>
    this.currentTableModel()
      .fieldModels.filter((model) => model.inlineConsolidatorForField)
      .map((model) => model.key),
  );

  private readonly inlineConsolidatorsRequiredValidator: ValidatorFn = (
    control: AbstractControl,
  ) => {
    return (control as FormArray<ExtractedRowForm>).controls.every((rowGroup) => {
      return this.inlineConsolidatorKeys().every(
        (key) =>
          !this.shouldConsolidate(key, rowGroup) ||
          isDefined(rowGroup.controls.rowData.controls[key].value.value),
      );
    })
      ? null
      : { undefinedConsolidators: true };
  };

  private shouldConsolidate(consolidatorKey: string, rowGroup: ExtractedRowForm) {
    const consolidatorForFieldKey = this.inlineConsolidatorKeyForFieldMap()[consolidatorKey];
    const fieldValue = rowGroup.controls.rowData.controls[consolidatorForFieldKey].value.value;
    const consolidator = this.currentTableModel().fieldModels.find(
      (model) => model.key === consolidatorKey,
    );

    return !!fieldValue && consolidator?.inlineConsolidatorRequired;
  }

  private readonly classificationViewValidators = [
    this.allRowsClassifiedValidator,
    this.inlineConsolidatorsRequiredValidator,
  ];

  readonly activeField = computed<ExtractionField | undefined>(() => {
    const cell = this.currentTableCell();
    return cell
      ? {
          model: getTableModelByColumnKey(
            this.batchStoreService.ctBatchDomain,
            cell.fieldKey,
          )!.fieldModels.find((model) => model.key === cell.fieldKey)!,
          control: cell.cellForm.controls.value,
        }
      : undefined;
  });

  constructor() {
    super();
    effect(() => {
      if (this.tableRows()) {
        this.addClassificationViewValidators();
        this.addStatusChangeListener();
        this.initPositionTypes();
      }
    });
  }

  ngOnInit() {
    this.pageNavigationService
      .navigate$('back')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.removeClassificationViewValidators());

    this.pageNavigationService
      .navigate$('continue')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.tableRows().valid) {
          this.removeClassificationViewValidators();
          this.continue.emit();
        }
      });

    this.toolBarService.setTableCreationEnabled(false);
  }

  protected onRowSelected(rowIndex: number | undefined) {
    const row = rowIndex !== undefined ? this.tableRows().controls[rowIndex] : undefined;
    this.currentRowToClassify.set(row);
  }

  private addClassificationViewValidators() {
    if (!this.tableRows().hasValidator(this.allRowsClassifiedValidator)) {
      this.tableRows().addValidators(this.classificationViewValidators);
    }
  }

  private removeClassificationViewValidators() {
    this.tableRows().removeValidators(this.classificationViewValidators);
  }

  private initPositionTypes() {
    this.tableRows().controls.forEach((rowGroup, rowIndex) => {
      rowGroup.controls.class.setValue(
        this.currentTableModel().rowClassification?.initialValueProvider?.(
          rowGroup.controls.rowData.getRawValue(),
          rowGroup.controls.class.value,
          rowIndex,
        ),
      );
    });

    const firstUnclassifiedRow = this.tableRows().controls.find((row) => !row.controls.class.value);
    this.currentRowToClassify.set(firstUnclassifiedRow);

    this.tableRows().controls.forEach((rowForm, index) => {
      rowForm.controls.class.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((_) => {
          this.currentRowToClassify.set(
            this.tableRows().controls.find((row, i) => i > index && !row.controls.class.value),
          );
        });
    });
  }

  private addStatusChangeListener() {
    const rows = this.tableRows();
    rows.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (rows.invalid) {
        this.pageNavigationService.disableNavigation(
          'continue',
          rows.hasError('unclassifiedRows')
            ? 'Bitte klassifiziere alle Positionen.'
            : 'Bitte vervollständige bzw. korrigiere deine Eingaben',
        );
      } else {
        this.pageNavigationService.enableNavigation('continue');
      }
    });
  }
}
